import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  BarChart3,
  ClipboardList,
  Ban,
  Zap,
  Coins,
  Wallet,
  Settings as SettingsIcon,
  Building2,
  UserPlus,
  ScrollText,
  Send,
  Mail,
  Scale,
  AlertTriangle,
  CalendarRange,
  Target,
  XCircle,
  Wrench,
  Headphones,
  Bell as BellIcon,
  LogOut,
  X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useTermsAccess } from "@/lib/termsAccess";
import NotificationBell from "@/components/NotificationBell";
import ProfileEditModal from "@/components/ProfileEditModal";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canAccess, canAccessPayroll } from "@/lib/permissions";
import { useHiringAccess } from "@/lib/hiringAccess";

export function useNavItems(slug) {
  const base = `/${slug}/app`;
  return [
    { section: "overview",      to: `${base}/overview`,      label: "Overview",      icon: LayoutDashboard, testId: "nav-overview" },
    { section: "agents",        to: `${base}/agents`,        label: "Agent Roster",  icon: Users,           testId: "nav-agents" },
    { section: "kpi",           to: `${base}/kpi`,           label: "KPI Tracker",   icon: BarChart3,       testId: "nav-kpi" },
    { section: "l10",           to: `${base}/l10`,           label: "L10 / EOS",     icon: ClipboardList,   testId: "nav-l10" },
    { section: "cancellations", to: `${base}/cancellations`, label: "Cancellations", icon: Ban,             testId: "nav-cancellations" },
    { section: "thank_you_cards", to: `${base}/thank-you-cards`, label: "Thank You Cards", icon: Mail,   testId: "nav-thank-you-cards" },
    { section: "hiring",        to: `${base}/hiring/pipeline`, label: "Hiring — Pipeline",   icon: Users,        testId: "nav-hiring-pipeline" },
    { section: "hiring",        to: `${base}/hiring/calendar`, label: "Hiring — Calendar",   icon: Users,        testId: "nav-hiring-calendar" },
    { section: "hiring",        to: `${base}/hiring/pending`,  label: "Hiring — Pending applicants", icon: Users, testId: "nav-hiring-pending" },
    { section: "hiring",        to: `${base}/hiring/settings`, label: "Hiring — Settings",        icon: Users,           testId: "nav-hiring" },
    { section: "collections", to: `${base}/collections`, label: "Collections", icon: Scale, testId: "nav-collections" },
    { section: "terms",       to: `${base}/commercial-accounts`, label: "Commercial Accounts", icon: Scale, testId: "nav-commercial-accounts" },
    { section: "dispatch_assist", to: `${base}/dispatch-assist`, label: "Dispatch Assist", icon: Zap,      testId: "nav-dispatch-assist" },
    { section: "tech_profiles_view", to: `${base}/tech-profiles`, label: "Tech Profiles", icon: Wrench,   testId: "nav-tech-profiles-view" },
    { section: "payroll",       to: `${base}/payroll`,       label: "Payroll",       icon: Coins,           testId: "nav-payroll" },
    { section: "csr_sales_log", to: `${base}/payroll/csr-sales`, label: "CSR Sales Log",   icon: ClipboardList,   testId: "nav-csr-sales" },
    { section: "payroll",       to: `${base}/payroll/csr-backpay`, label: "CSR Backpay",   icon: CalendarRange,   testId: "nav-csr-backpay" },
    { section: "comp",          to: `${base}/comp`,          label: "COO Commission", icon: Wallet,          testId: "nav-comp" },
    { section: "settings",      to: `${base}/settings`,      label: "Settings",      icon: SettingsIcon,    testId: "nav-settings" },
  ];
}

export default function Sidebar({ tenant, isOpen = false, onClose }) {
  const { user, logout } = useAuth();
  const hiringAccess = useHiringAccess(user);
  const termsAccess = useTermsAccess(user);  // CC-351
  const nav = useNavigate();
  const { slug } = useParams();
  const isAdmin = user && user.role === "platform_admin";
  const isOwner = user && (user.role === "owner" || user.role === "administrative");  // CC-261
  const showAdminSection = isAdmin || isOwner;
  const items = useNavItems(slug || tenant?.slug || "");
  const closeOnNav = () => { if (onClose) onClose(); };

  // Sidebar unread-lead badge — platform_admin only. Polls every 60s, and
  // reloads when the tab regains focus so the number stays fresh without
  // spamming the API.
  const [unreadLeads, setUnreadLeads] = useState(0);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/leads/unread_count");
        if (alive) setUnreadLeads(data?.unread_count || 0);
      } catch { /* silent — badge stays at last known value */ }
      try {
        const { data } = await api.get("/admin/platform-alerts/unread_count");
        if (alive) setUnreadAlerts(data?.unread_count || 0);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAdmin]);

  return (
    <>
      <div
        onClick={onClose}
        data-testid="sidebar-backdrop"
        className={`lg:hidden fixed inset-0 bg-black/50 z-30 transition-opacity ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-[240px] flex flex-col text-[color:var(--cc-navy-text)]
          transform transition-transform duration-200 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:static lg:translate-x-0 lg:shrink-0
        `}
        style={{ background: "var(--cc-navy)" }}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-[color:var(--cc-navy-border)] flex items-center justify-between">
          <Logo withWordmark tone="dark" />
          <button
            onClick={onClose}
            data-testid="sidebar-close-btn"
            className="lg:hidden p-1.5 rounded-md hover:bg-[color:var(--cc-navy-card)]"
            aria-label="Close menu"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        {slug && (
          <div className="px-5 py-4 border-b border-[color:var(--cc-navy-border)]">
            <div className="text-[10px] uppercase tracking-[.14em] text-[color:var(--cc-navy-muted)] mb-1.5">
              Current Tenant
            </div>
            <div className="text-[13px] font-semibold leading-tight" data-testid="current-tenant-name">
              {tenant?.business_name || "—"}
            </div>
            <div className="text-[11px] text-[color:var(--cc-navy-muted)] mt-0.5">{tenant?.location}</div>
            {tenant?.slug && (
              <div className="text-[10px] font-mono-data text-[color:var(--cc-amber)] mt-1">/{tenant.slug}</div>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {slug && items.filter((it) => {
            // pt.83.1 — Payroll uses email-allowlist-aware gate so owner
            // (Nathanael) and administrative (Missie) can still see it if
            // their email is on PAYROLL_EDITOR_EMAILS / _APPROVER_EMAILS.
            if (it.section === "payroll") return canAccessPayroll(user);
            if (it.section === "terms") return termsAccess;
            // CC-148 — stage owners see Pipeline + Calendar; Settings stays role-gated.
            if (it.section === "hiring" && it.testId !== "nav-hiring") return hiringAccess;
            return canAccess(user?.role, it.section);
          }).map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={closeOnNav}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Icon size={17} strokeWidth={1.6} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}

          {showAdminSection && (
            <>
              <div className="mt-5 mb-1.5 px-3 text-[10px] uppercase tracking-[.14em] text-[color:var(--cc-navy-muted)]">
                {isAdmin ? "Platform Admin" : "Admin"}
              </div>
              {isAdmin && (
              <NavLink
                to="/admin/tenants"
                onClick={closeOnNav}
                data-testid="nav-admin-tenants"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Building2 size={17} strokeWidth={1.6} />
                <span>Tenants</span>
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/leads"
                onClick={closeOnNav}
                data-testid="nav-admin-leads"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <UserPlus size={17} strokeWidth={1.6} />
                <span className="flex-1">Leads</span>
                {unreadLeads > 0 && (
                  <span
                    data-testid="nav-admin-leads-badge"
                    className="min-w-[20px] h-[18px] px-1.5 rounded-full bg-[#ef5757] text-white text-[10px] font-bold flex items-center justify-center leading-none"
                  >
                    {unreadLeads > 99 ? "99+" : unreadLeads}
                  </span>
                )}
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/audit-log"
                onClick={closeOnNav}
                data-testid="nav-admin-audit-log"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <ScrollText size={17} strokeWidth={1.6} />
                <span>Audit Log</span>
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/recovery"
                onClick={closeOnNav}
                data-testid="nav-admin-recovery"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Send size={17} strokeWidth={1.6} />
                <span>Recovery Sequences</span>
              </NavLink>
              )}
              <NavLink
                to="/admin/tech-profiles"
                onClick={closeOnNav}
                data-testid="nav-admin-tech-profiles"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Wrench size={17} strokeWidth={1.6} />
                <span>Tech Profiles {isOwner && <span className="text-[9.5px] uppercase ml-1 opacity-70">(edit)</span>}</span>
              </NavLink>
              <NavLink
                to="/admin/csr-staff"
                onClick={closeOnNav}
                data-testid="nav-admin-csr-staff"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Headphones size={17} strokeWidth={1.6} />
                <span>CSR Staff {isOwner && <span className="text-[9.5px] uppercase ml-1 opacity-70">(edit)</span>}</span>
              </NavLink>
              {isAdmin && (
              <NavLink
                to="/admin/platform-alerts"
                onClick={closeOnNav}
                data-testid="nav-admin-platform-alerts"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <BellIcon size={17} strokeWidth={1.6} />
                <span className="flex-1">Platform Alerts</span>
                {unreadAlerts > 0 && (
                  <span
                    data-testid="nav-admin-platform-alerts-badge"
                    className="min-w-[20px] h-[18px] px-1.5 rounded-full bg-[#ef5757] text-white text-[10px] font-bold flex items-center justify-center leading-none"
                  >
                    {unreadAlerts > 99 ? "99+" : unreadAlerts}
                  </span>
                )}
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/ingest-warns"
                onClick={closeOnNav}
                data-testid="nav-admin-ingest-warns"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <AlertTriangle size={17} strokeWidth={1.6} />
                <span>Ingest Warnings</span>
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/tech-goals-audit"
                onClick={closeOnNav}
                data-testid="nav-admin-tech-goals-audit"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <Target size={17} strokeWidth={1.6} />
                <span>Tech Goals Audit</span>
              </NavLink>
              )}
              {isAdmin && (
              <NavLink
                to="/admin/cancel-attribution-audit"
                onClick={closeOnNav}
                data-testid="nav-admin-cancel-attribution-audit"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#1e2c48] text-white"
                      : "text-[color:var(--cc-navy-muted)] hover:bg-[color:var(--cc-navy-card)] hover:text-[color:var(--cc-navy-text)]"
                  }`
                }
              >
                <XCircle size={17} strokeWidth={1.6} />
                <span>Cancel Attribution Audit</span>
              </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-[color:var(--cc-navy-border)] space-y-2.5">
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            data-testid="sidebar-profile-btn"
            title="Edit my profile"
            className="w-full text-left group rounded-md px-2 py-1.5 hover:bg-[color:var(--cc-navy-card)] transition-colors"
          >
            <div className="text-[11.5px] text-[color:var(--cc-navy-muted)] group-hover:text-[color:var(--cc-navy-text)] flex items-center justify-between gap-2">
              <span className="truncate">{user?.name}</span>
              <span className="text-[10px] font-mono-data uppercase tracking-widest opacity-70">Edit</span>
            </div>
            <div className="text-[11px] font-mono-data uppercase tracking-wider text-[color:var(--cc-amber)] mt-0.5">
              {user?.role}
            </div>
          </button>
          {slug && slug !== "admin" && user?.tenant_id && <NotificationBell />}
          <button
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center justify-center gap-2 text-[12.5px] font-semibold py-2 rounded-md border border-[color:var(--cc-navy-border)] hover:bg-[color:var(--cc-navy-card)] transition-colors"
          >
            <LogOut size={14} strokeWidth={1.6} />
            Sign out
          </button>
        </div>
      </aside>
      {profileOpen && <ProfileEditModal onClose={() => setProfileOpen(false)} />}
    </>
  );
}
